import { useMemo, useRef, useState } from 'react';
import { useAuthContext } from '../../hooks/useAuthContext';
import TranslationInput from '../ItemEditor/TranslationInput';
import Button from '../Button/Button';
import ImageUpload from '../ImageUpload/ImageUpload';
import BlogSectionList from './BlogSectionList';
import { BLOG_SECTION_TYPES, createBlogSection } from './blogSectionTypes';
import { apiFetch } from '../../utils/api';
import { uploadToImageKit } from '../../utils/imageKitUpload';
import { emptyTranslation } from '../../utils/translationUtils';
import '../ItemEditor/ItemEditor.css';
import '../ItemEditor/SectionEditor.css';
import './BlogPostEditor.css';

const POST_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
];

export const emptyBlogPost = () => ({
  title: emptyTranslation(),
  image: '',
  imageFileId: null,
  status: 'draft',
  sections: [createBlogSection('text')],
});

const validate = (post) => {
  if (!post.title.en.trim()) return 'Title (EN) is required.';
  const incompleteIdx = post.sections.findIndex(s => !BLOG_SECTION_TYPES[s.type].isComplete(s));
  if (incompleteIdx !== -1) return `Section ${incompleteIdx + 1} needs English content.`;
  return null;
};

const readError = async (res, fallback) => {
  try {
    const body = await res.json();
    return body?.error || fallback;
  } catch {
    return fallback;
  }
};

const BlogPostEditor = ({ postId, initialPost, onCreated }) => {
  const isEditMode = !!postId;
  const { user } = useAuthContext();
  const baseline = useRef(initialPost ?? emptyBlogPost());
  const [post, setPost] = useState(baseline.current);
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const hasChanges = !isEditMode || !!imageFile || JSON.stringify(post) !== JSON.stringify(baseline.current);
  const imagePreviewSrc = useMemo(
    () => (imageFile ? URL.createObjectURL(imageFile) : post.image || null),
    [imageFile, post.image]
  );

  const updatePost = (changes) => {
    setPost(prev => ({ ...prev, ...changes }));
    setSaved(false);
  };

  const handleSubmit = async () => {
    const validationError = validate(post);
    if (validationError) { setError(validationError); return; }

    setSubmitting(true);
    setError(null);

    try {
      const uploaded = imageFile ? await uploadToImageKit(imageFile, user.token) : null;
      const postToSave = uploaded ? { ...post, image: uploaded.url, imageFileId: uploaded.fileId } : post;

      const res = await apiFetch(isEditMode ? `/api/admin/blog/${postId}` : '/api/admin/blog', {
        method: isEditMode ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify(postToSave),
      });

      if (!res.ok) throw new Error(await readError(res, 'Failed to save the post'));

      const savedPost = await res.json();
      if (!isEditMode) {
        onCreated?.(savedPost._id);
        return;
      }
      baseline.current = postToSave;
      setPost(postToSave);
      setImageFile(null);
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="blog-post-editor">
      <TranslationInput
        label="Title"
        value={post.title}
        onChange={(lang, val) => updatePost({ title: { ...post.title, [lang]: val } })}
        disabled={submitting}
      />

      <span className="translation-label">Cover image (optional)</span>
      <ImageUpload
        previewSrc={imagePreviewSrc}
        onSelect={(file) => { setImageFile(file); setSaved(false); }}
        onRemove={() => { setImageFile(null); updatePost({ image: '', imageFileId: null }); }}
        disabled={submitting}
      />

      <div className="blog-post-editor-status">
        <span className="translation-label">Visibility</span>
        <div className="section-type-toggle">
          {POST_STATUSES.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={post.status === value ? 'active' : ''}
              onClick={() => updatePost({ status: value })}
              disabled={submitting}
            >
              {label}
            </button>
          ))}
        </div>
        <span className="blog-post-editor-hint">
          {post.status === 'draft' ? 'Only admins can see drafts.' : 'Visible to everyone on the blog.'}
        </span>
      </div>

      <span className="translation-label">Sections</span>
      <BlogSectionList
        sections={post.sections}
        onChange={(sections) => updatePost({ sections })}
        disabled={submitting}
      />

      <div className="item-editor-submit-area">
        {saved && <div className="form-message success">Changes saved!</div>}
        {error && <div className="form-message error">{error}</div>}
        <Button variant="primary" onClick={handleSubmit} disabled={submitting || !hasChanges}>
          {submitting ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Create Post')}
        </Button>
      </div>
    </div>
  );
};

export default BlogPostEditor;
